"use client"

import { create } from "zustand"

import type { ModalName, ModalProps } from "@/components/modal/registry"

export type ModalInstance = {
  key: string
  name: ModalName
  props: ModalProps[ModalName]
  closing: boolean
  result?: unknown
}

type ModalState = {
  stack: ModalInstance[]
  open: <N extends ModalName>(
    name: N,
    props?: ModalProps[N]
  ) => Promise<unknown>
  close: (key: string, result?: unknown) => void
  remove: (key: string) => void
  closeAll: () => void
}

// Resolvers live outside Zustand: functions must not go into state.
const resolvers = new Map<string, (result: unknown) => void>()
let nextKey = 0

export const useModalStore = create<ModalState>((set, get) => ({
  stack: [],

  open: (name, props) =>
    new Promise((resolve) => {
      const key = "m" + ++nextKey
      resolvers.set(key, resolve)
      set((state) => ({
        stack: [
          ...state.stack,
          {
            key,
            name,
            props: (props ?? {}) as ModalProps[ModalName],
            closing: false
          }
        ]
      }))
    }),

  // Mark closing first so Radix can play the exit animation; remove() runs from onExited.
  close: (key, result) =>
    set((state) => ({
      stack: state.stack.map((modal) =>
        modal.key === key ? { ...modal, closing: true, result } : modal
      )
    })),

  remove: (key) => {
    const modal = get().stack.find((item) => item.key === key)
    resolvers.get(key)?.(modal?.result)
    resolvers.delete(key)
    set((state) => ({ stack: state.stack.filter((item) => item.key !== key) }))
  },

  closeAll: () =>
    set((state) => ({
      stack: state.stack.map((modal) => ({ ...modal, closing: true }))
    }))
}))
